/**
 * WhatsNew API — shared between student and teacher apps.
 */

/** How the default card is presented — see `presentation` below. */
export type WhatsNewPresentation = 'default' | 'close-only';

export interface WhatsNewTip {
    id: string;
    /** 'selected' = no criteria audience; the card reaches only the users an
     *  admin pinned to it, and rides along in whichever app they open. */
    appTarget: 'student' | 'teacher' | 'all' | 'selected';
    /** The card's only heading (the dialog's header bar). Empty = no header. */
    title: string;
    body: string;
    imageUrl?: string;
    /** How the image fills its fixed-height band. Default 'contain'. */
    imageFit?: 'contain' | 'full-bleed';
    /** Which controls the default card carries. 'default' = the Dismiss +
     *  Remind-me-later footer; 'close-only' = no footer, the top-right ✕ is the
     *  single control (and dismisses). Absent ⇒ 'default'. */
    presentation?: WhatsNewPresentation;
    priority: number;
    publishAt: string;
    /** Behavioral trigger, when configured (e.g. subject-daily-solve). Used to derive the "Try Now" link. */
    condition?: {
        type: 'subject-daily-solve';
        targetSubject: string;
        targetMinAttempted: number;
        excludeSubject: string;
        excludeMaxAttempted: number;
        windowDays: number;
    };
    /** Custom experience (Tier C): render the registered component for this key instead of the default card. */
    experienceKey?: string;
    experienceData?: Record<string, unknown>;
    /** Data the server already built for this experience, shipped with the tip so
     *  the card renders without a fetch. Present only on the displayed tip, and
     *  only for experiences with a server-side builder — treat it as a cache
     *  seed, never as the only source (see functions/src/whatsNew/experiencePayloads.ts). */
    experiencePayload?: Record<string, unknown>;
    /** A code-owned card addressed to this user alone (e.g. a feedback reply).
     *  Shown even inside the once-a-day cooldown, and doesn't spend it. */
    personal?: boolean;
}

export interface WhatsNewView {
    id: string;
    userId: string;
    tipId: string;
    seenAt: string;
    lastSeenAt: string;
    nextEligibleAt?: string;
}

export type SnoozeMode = 'dismiss' | 'remind-later';

/** The actual control the user acted on (finer-grained than SnoozeMode). 'completed' = a custom experience finished. */
export type TipAction = 'dismiss' | 'remind-later' | 'try-now' | 'close' | 'completed';

export interface TipsResponse {
    tips: WhatsNewTip[];
    totalUnseen: number;
    totalSeen: number;
}

export interface WhatsNewApiClient {
    fetchApi: <T>(endpoint: string, options?: RequestInit) => Promise<T>;
}

export function createWhatsNewApi(client: WhatsNewApiClient) {
    return {
        /** Fetch unseen tips (lightweight, for dialog trigger). */
        getUnseenTips: (params: { app: string; platform?: string; appVersion?: string; personalOnly?: boolean }) => {
            const q = new URLSearchParams();
            q.set('app', params.app);
            // Only the cards addressed to this user (see WhatsNewTip.personal).
            if (params.personalOnly) q.set('personalOnly', '1');
            if (params.platform) q.set('platform', params.platform);
            if (params.appVersion) q.set('appVersion', params.appVersion);
            return client.fetchApi<TipsResponse>(`/whats-new/tips?${q.toString()}`);
        },

        /**
         * Act on a tip (dismiss / remind-later / try-now / close). Sends the
         * real control as `action` plus `dwellMs` (time-to-click) for
         * effectiveness analytics; the server derives the snooze cooldown.
         */
        act: (tipId: string, action: TipAction, dwellMs?: number) =>
            client.fetchApi<WhatsNewView>('/whats-new/snooze', {
                method: 'POST',
                body: JSON.stringify({ tipId, action, ...(dwellMs != null ? { dwellMs } : {}) }),
            }),

        /** Record that a tip was shown (fire-and-forget reach signal). */
        recordImpression: (tipId: string) =>
            client.fetchApi<WhatsNewView>('/whats-new/impression', {
                method: 'POST',
                body: JSON.stringify({ tipId }),
            }),

        /**
         * Submit a survey response. The server validates the answers against the
         * tip's survey definition, stores them as app_feedback, and snoozes the
         * tip 'completed'. See docs/FeatureDesigns/whats-new-surveys.md.
         */
        submitSurvey: (tipId: string, answers: Record<string, unknown>, dwellMs?: number) =>
            client.fetchApi<{ threadId: string }>('/whats-new/survey', {
                method: 'POST',
                body: JSON.stringify({ tipId, answers, ...(dwellMs != null ? { dwellMs } : {}) }),
            }),
    };
}

export type WhatsNewApi = ReturnType<typeof createWhatsNewApi>;
